import { AxiosProgressEvent } from "axios";
import { api, formDataApi } from "./api";

export type ContentFormat = "BLOCK" | "FREETEXT";

// 본문 저장 옵션 (없으면 서버가 게시글의 현재 형식을 유지)
export interface PostContentOptions {
  contentFormat: ContentFormat;
  contentEn?: string | null;
}

export interface Comment {
  commentId: number;
  content: string;
  name: string;
  createdAt?: string;
  modifiedAt?: string;
}

export interface Post {
  id: number;
  categoryId: number;
  title: string;
  writer: string;
  slides: any[];
  isActive: boolean;
  createdAt: string;
  modifiedAt: string;
  content: string;
  contentFormat?: ContentFormat; // 본문 형식 (BLOCK: EditorJS 블록 JSON / FREETEXT: HTML)
  contentEn?: string | null; // FreeText 영문 본문
  postThumbnail: string;
  comments: Comment[];
  isSecret: boolean;
  ocspstLinks?: OcspstLink[]; // 소아고형암 온라인 자문시스템 실제 증례 링크 (한 게시글에 여러 증례)
  referenceText?: string | null; // 참고문헌 (본문 아래 자유 텍스트)
}

// 게시글에 연결된 소아고형암 온라인 자문시스템(OCSPST) 증례. 한 증례는 한 게시글에만 연결된다.
export interface OcspstLink {
  ocspstLinkId: number;
  ocspstCaseId: number;
  url: string;
}

export interface SearchedPost {
  id: number;
  title: string;
  boardId: number;
  categoryType: string;
  parentName: string;
  grandParentName: string;
  grandGrandParentName: string;
  createdAt: string;
  updatedAt: string;
}

export interface Photo {
  imageName: string;
  photoId?: number;
  postId: number;
  caption: string;
  gender: "M" | "F";
  age: string;
  site: string;
  zindanName: string;
  pathFileName?: string;
  originalFileName?: string;
  postCaseId?: number;
  file?: any;
}

export interface GrossImage {
  postId: number;
  grossPhotoId?: number;
  originalFileName?: string;
  pathFileName?: string;
  caption?: string;
  gender?: "M" | "F" | "U";
  age?: string;
  site?: string;
  zindanName?: string;
  description?: string;
  listOrder?: number;
  postCaseId?: number;
  createdAt?: string;
  modifiedAt?: string;
  file?: any;
}

export interface PostSearchParam {
  title: string | null;
}

export const getPostList = async (
  page: number,
  size: number,
  categoryId: number | null,
  criteria: string,
  sort: string,
  searchParams?: PostSearchParam | null,
  hasThumbnail?: boolean | null,
) => {
  try {
    const title = searchParams?.title;
    let params = { page, size, categoryId, criteria, sort, title, hasThumbnail };

    const { data } = await api().get("/post", { params });
    return data.data;
  } catch (e) {
    console.log(e);
    return [];
  }
};

// 집담회용 조회 api
export const getPostListWithTotal = async (
  page: number,
  size: number,
  categoryId: number | null,
  criteria: string,
  sort: string,
  searchParams?: PostSearchParam | null,
  hasThumbnail?: boolean | null,
) => {
  try {
    const title = searchParams?.title;
    let params = { page, size, categoryId, criteria, sort, title, hasThumbnail };

    const { data } = await api().get("/post", { params });
    return data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const getPostsBySubcategory = async (
  page: number,
  size: number,
  categoryId: number | null,
  criteria: string,
  sort: string,
  searchParams?: PostSearchParam | null,
  hasThumbnail?: boolean | null,
) => {
  try {
    const title = searchParams?.title;
    let params = { page, size, categoryId, criteria, sort, title, hasThumbnail };

    const { data } = await api().get("/posts/sub-category", { params });
    console.log(data);
    return data.data;
  } catch (e) {
    console.log(e);
    return [];
  }
};

// 소아고형암 온라인 자문시스템(OCSPST) 증례 링크가 걸린 게시글 (게시판 구분 없이, 읽기 권한 있는 글만)
export const getOcspstLinkedPosts = async (
  page: number,
  size: number,
  criteria: string,
  sort: string,
): Promise<Post[]> => {
  try {
    const { data } = await api().get("/posts/ocspst-linked", { params: { page, size, criteria, sort } });
    return data.data ?? [];
  } catch (e) {
    console.log(e);
    return [];
  }
};

// 게시글 상세 조회 실패 resultCode
// - 비밀글: SECRET_PASSWORD_REQUIRED / SECRET_PASSWORD_INVALID (비밀번호 모달 대상)
// - 권한 부족: 403 (게시판/카테고리 권한 없음), 401 (인증 문제)
export const SECRET_PASSWORD_REQUIRED = "SECRET_PASSWORD_REQUIRED";
export const SECRET_PASSWORD_INVALID = "SECRET_PASSWORD_INVALID";

export const isSecretPostResponse = (data: any) =>
  data?.resultCode === SECRET_PASSWORD_REQUIRED || data?.resultCode === SECRET_PASSWORD_INVALID;

export const isPostAccessDenied = (data: any) =>
  data?.resultCode === "401" || data?.resultCode === "403";

export const isPostDetailBlocked = (data: any) =>
  isSecretPostResponse(data) || isPostAccessDenied(data);

export const getPostData = async (postId: number, password?: string) => {
  try {
    const { data } = await api().get(`/post/${postId}`, {
      params: { password }
    });
    return data.data;
  } catch (error: any) {
    if (isPostDetailBlocked(error.response?.data)) {
      return error.response.data;
    }
    throw error;
  }
};

export const createPost = async (categoryId: number) => {
  try {
    const { data } = await api().post(`/post/temporary/${categoryId}`);
    return data.data;
  } catch (e) {
    console.log(e);
    return "";
  }
};

export const postPost = async (
  postId: number,
  title: string | null,
  content: string | null,
  options?: PostContentOptions
) => {
  try {
    const body = { postId, title, content, ...options };
    const { data } = await api().post("/post", body);
    return data;
  } catch (e) {
    console.log(e);
    return "";
  }
};

export const postPostTable = async (params: {
  categoryId: number;
  meetAge: string;
  meetDiagnosis: string;
  meetGender: string;
  meetKppNumber: string;
  meetOrgan: string;
  meetOrganization: string;
}) => {
  try {
    const body = { ...params };
    const { data } = await api().post("/post/table", body);
    return data;
  } catch (e) {
    console.log(e);
  }
};

export const postUpdatePostTable = async (params: {
  postId: number;
  age: string;
  diagnosis: string;
  gender: string;
  kppNumber: string;
  organ: string;
  organization: string;
}) => {
  try {
    const body = { ...params };
    const { data } = await api().post("/post/table/update", body);
    return data;
  } catch (e) {
    console.log(e);
  }
};

export const putPost = async (
  postId: number,
  title: string,
  content: string | null,
  options?: PostContentOptions
) => {
  try {
    const body = { postId, title, content, ...options };
    const { data } = await api().put("/post", body);
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const deletePost = async (postId: number) => {
  try {
    const { data } = await api().delete(`/post/${postId}`);
    return data;
  } catch (e) {
    console.log(e);
  }
};

// autoCorrect: false 면 서버가 오타 교정을 하지 않고 입력한 검색어 그대로 조회한다
export const searchPost = async (title: string, autoCorrect: boolean = true) => {
  try {
    const params = { title, size: 9999, autoCorrect }; //size 수정 필요(페이징 구현 완료 후)
    const { data } = await api().get("/post/search", { params });
    return data.data;
  } catch (e) {
    console.log(e);
    return [];
  }
};

export const reorderPosts = async (postList: { postId: any; newOrder: number; }[]) => {
  try {
    const body = postList;
    const { data } = await api().post(`/post/reorder`, body);
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getAuthors = async ({ postId }: { postId: number }) => {
  try {
    if(!postId) return;

    const { data } = await api().get(`/post/authors/${postId}`);
    return data.data;
  } catch (e) {
    console.log(e);
    return "";
  }
};

export const createAuthors = async ({
  postId,
  authors,
}: {
  postId: number;
  authors: number[];
}) => {
  try {
    const body = { postId, authors };
    const { data } = await api().post(`/post/authors`, body);
    return data.data;
  } catch (e) {
    console.log(e);
    return "";
  }
};

export const deleteAuthor = async ({
  postId,
  authorId,
}: {
  postId: number;
  authorId: number;
}) => {
  try {
    const body = { postId, authorId };
    const { data } = await api().post(`/post/authors/delete`, body);
    return data.data;
  } catch (error) {
    console.log(error);
    return "";
  }
};

export const getImage = async (filename: string) => {
  try {
    const { data } = await api().get(`/photo/${filename}`);
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getImageList = async (postId: number) => {
  try {
    const params = { postId };
    const { data } = await api().get("/photo/list", { params });
    return data.data;
  } catch (e) {
    console.log(e);
    return [];
  }
};

export type UploadProgressHandler = (ratio: number) => void;

const toUploadProgress = (onProgress?: UploadProgressHandler) =>
  onProgress
    ? (e: AxiosProgressEvent) => {
        if (e.total) onProgress(e.loaded / e.total);
      }
    : undefined;

export const postImage = async (photo: Photo, onProgress?: UploadProgressHandler) => {
  try {
    const { file, ...params } = photo;
    const body = { file: file.file };
    const { data } = await formDataApi().post("/photo", body, {
      params,
      onUploadProgress: toUploadProgress(onProgress),
    });
    return data;
  } catch (e) {
    console.log(e);
    return { success: false };
  }
};

// 에디터 내 이미지 업로드용 API
export const postEditorImage = async (postId: number | string, file: File) => {
  try {
    const formData = new FormData();
    formData.append('postId', String(postId));
    formData.append('file', file);

    const response = await formDataApi().post("/photo/editor", formData);
    const data = response.data;

    if (data?.file?.url && data.file.url.startsWith('/')) {
      data.file.url = process.env.REACT_APP_BACKEND_URL + data.file.url;
    }

    return data;
  } catch (e) {
    console.error('이미지 업로드 실패:', e);
    return { success: 0 };
  }
};

export const patchImageData = async (imageData: Photo) => {
  try {
    const data = await api().patch("/photo", imageData);
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
};

// 게시글에 소아고형암 온라인 자문시스템(OCSPST) 증례 링크 추가 / 해제. 변경 후 목록을 돌려준다.
// 이미 다른 게시글에 연결된 증례면 409 (메시지는 e.response.data.message)
export const postOcspstLink = async (postId: number, url: string): Promise<OcspstLink[]> => {
  const { data } = await api().post(`/post/${postId}/ocspst-links`, { url });
  return data.data;
};

export const deleteOcspstLink = async (ocspstLinkId: number): Promise<OcspstLink[]> => {
  const { data } = await api().delete(`/post/ocspst-links/${ocspstLinkId}`);
  return data.data;
};

export const deleteImage = async (photoId: number) => {
  try {
    const data = await api().delete(`/photo/${photoId}`);
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getGrossImageList = async (postId: number): Promise<GrossImage[]> => {
  try {
    const params = { postId };
    const { data } = await api().get("/gross-photo/list", { params });
    return data.data ?? [];
  } catch (e: any) {
    if (e.response?.status === 404) {
      return [];
    }
    console.error("getGrossImageList error:", e);
    return [];
  }
};

export const getGrossImage = async (filename: string) => {
  try {
    const { data } = await api().get(`/gross-photo/file/${filename}`);
    return data;
  } catch (e) {
    console.error("getGrossImage error:", e);
    return false;
  }
};

export const postGrossImage = async (
  grossImage: GrossImage,
  onProgress?: UploadProgressHandler
) => {
  try {
    const { file, ...params } = grossImage;
    const body = { file: file.file };
    const { data } = await formDataApi().post("/gross-photo", body, {
      params,
      onUploadProgress: toUploadProgress(onProgress),
    });
    return { success: true, data: data.data };
  } catch (e: any) {
    console.error("postGrossImage error:", e);
    const status = e.response?.status;
    let message = "육안 이미지 등록 중 오류가 발생했습니다.";
    if (status === 400) message = "이미지 파일만 업로드 가능합니다.";
    else if (status === 401) message = "권한이 없습니다.";
    else if (e.response?.data?.message) message = e.response.data.message;
    return { success: false, message };
  }
};

export const updateGrossImage = async (grossImage: GrossImage) => {
  try {
    const body = {
      grossPhotoId: grossImage.grossPhotoId,
      caption: grossImage.caption,
      gender: grossImage.gender,
      age: grossImage.age,
      site: grossImage.site,
      zindanName: grossImage.zindanName,
      description: grossImage.description,
    };
    const { data } = await api().post("/gross-photo/update", body);
    return { success: true, data: data?.data };
  } catch (e: any) {
    console.error("updateGrossImage error:", e);
    const status = e.response?.status;
    let message = "육안 이미지 수정 중 오류가 발생했습니다.";
    if (status === 401) message = "권한이 없습니다.";
    else if (status === 404) message = "해당하는 게시물이 없습니다.";
    else if (e.response?.data?.message) message = e.response.data.message;
    return { success: false, message };
  }
};

export const deleteGrossImage = async (grossPhotoId: number) => {
  try {
    const params = { grossPhotoId };
    const { data } = await api().post("/gross-photo/delete", null, { params });
    return { success: true, data };
  } catch (e: any) {
    console.error("deleteGrossImage error:", e);
    const message =
      e.response?.data?.message || "육안 이미지 삭제 중 오류가 발생했습니다.";
    return { success: false, message };
  }
};

export const deleteGrossImageList = async (grossPhotoIds: number[]) => {
  try {
    const { data } = await api().post("/gross-photo/delete/list", grossPhotoIds);
    return { success: true, data };
  } catch (e: any) {
    console.error("deleteGrossImageList error:", e);
    const message =
      e.response?.data?.message || "육안 이미지 삭제 중 오류가 발생했습니다.";
    return { success: false, message };
  }
};

export interface grossPhotoOrderType {
  grossPhotoId: number;
  listOrder: number;
}

export const putGrossPhotoOrderByList = async (
  grossPhotoList: grossPhotoOrderType[]
) => {
  try {
    const body = grossPhotoList;
    const { data } = await api().put("/gross-photo/listOrder/list", body);
    return data;
  } catch (e) {
    console.log(e);
  }
};

// 같은 게시글 내에서 이미지(photo) <-> 육안 이미지(gross-photo) 일괄 변환/이동. body: { ids: [1,2,3] }
export const convertPhotoToGross = async (photoIds: number[]) => {
  try {
    const { data } = await api().post(`/media-conversion/photo-to-gross`, { ids: photoIds });
    return { success: true, data };
  } catch (e: any) {
    console.error("convertPhotoToGross error:", e);
    const message =
      e.response?.data?.message || "그로스 이미지로 이동 중 오류가 발생했습니다.";
    return { success: false, message };
  }
};

export const convertGrossToPhoto = async (grossPhotoIds: number[]) => {
  try {
    const { data } = await api().post(`/media-conversion/gross-to-photo`, { ids: grossPhotoIds });
    return { success: true, data };
  } catch (e: any) {
    console.error("convertGrossToPhoto error:", e);
    const message =
      e.response?.data?.message || "이미지로 이동 중 오류가 발생했습니다.";
    return { success: false, message };
  }
};

export const postAttachment = async (postId: number, formData: FormData) => {
  try {
    const { data } = await api().post(`/post/${postId}/file`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  } catch (e) {
    console.error("Error uploading files:", e);
    throw e;
  }
};

export const deleteAttachment = async (postId: number, filename: string) => {
  try {
    const body = { postId, filename };
    const { data } = await api().post(`/post/file`, body);
    return data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

// ── 참고문헌 (게시글당 자유 텍스트 하나, 비우면 삭제. 실패하면 서버 메시지를 담은 에러를 던진다) ──
export const putPostReferenceText = async (postId: number, referenceText: string): Promise<string | null> => {
  const { data } = await api().put(`/post/${postId}/references`, { referenceText });
  return data.data?.referenceText ?? null;
};

export const getAttachment = async (postId: number) => {
  try {
    const { data } = await api().get(`/post/${postId}/file`);
    return data.data;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const downloadAttachment = async (fileName: string) => {
  try {
    const { data } = await api().get(`/post/file/${fileName}`, {
      responseType: "blob",
    });
    return data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const downloadAttachmentPreview = async (fileName: string) => {
  try {
    const { data } = await api().get(`/post/file-preview/${fileName}`, {
      responseType: "blob",
    });
    return data;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const changeCategory = async (postList: { postId: number; categoryId: number; listOrder: number; }[]) => {
  try {
    await api().post(`/post/change-category/batch`, postList);
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getLatestPost = async() => {
  try {
    const { data } = await api().get('/post/recent-one');
    return data.data;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getBestPost = async() => {
  try {
    const { data } = await api().get('/post/best');
    return data.data;
  } catch (e) {
    console.log(e);
    return [];
  }
};

//게시물 열람 시 조회 기록 저장
export const viewPost = async(postId: number) => {
  try {
    const { data } = await api().post(`/post/${postId}/view`);
    return data;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const setPassword = async (postId: number, isSecret: boolean, secretPassword?: string) => {
  try {
    const body = { postId, isSecret, secretPassword };
    const { data } = await api().post('/post/secret', body);
    return { success: true, data };
  } catch (e: any) {
    console.error(e);
    const errorMessage = e.response?.data?.message || '비밀글 설정 중 오류가 발생했습니다.';
    return { success: false, message: errorMessage };
  }
};

export const addConference = async (postId: number, categoryId: number) => {
  try {
    const body = { postId, categoryId };
    const { data } = await api().post('/conference-post', body);
    return { success: true, data };
  } catch (e: any) {
    console.error(e);
    const errorMessage = e.response?.data?.message || '학술강좌 등록 중 오류가 발생했습니다.';
    return { success: false, message: errorMessage };
  }
};

export const getConferencePosts = async () => {
  try {
    const { data } = await api().get('/conference-post/list');
    return { success: true, data: data.data };
  } catch (e: any) {
    console.error(e);
    const errorMessage = e.response?.data?.message || '학술강좌 조회 중 오류가 발생했습니다.';
    return { success: false, message: errorMessage };
  }
};